import type { RewindDecision } from '@/lib/code-sessions/rewind-policy';

import type { SessionTurnState } from './session-turn-state';

import { canRewindFromTurn } from '@/lib/code-sessions/rewind-policy';
import { deterministicPromptId } from '@/lib/code-sessions/turn-utils';

// What the transcript shows for a rewind. OpenCode deletes rewound messages
// only when the rewind is committed, and the session caches keep messages it
// has deleted, so visibility keys off our turn state, never off whether
// OpenCode still has a message.

type RewindTurn = { id: string; index: number; discardedAt?: string | null; unsent?: boolean };

/**
 * Edit from here on every visible saved turn, keyed by turn ID. Unsaved
 * prompts have no turn to rewind to yet, and a request already in flight
 * blocks the rest.
 */
export function messageEdits(
  state: SessionTurnState,
  busyReason: string | undefined,
): Map<string, RewindDecision> {
  const edits = new Map<string, RewindDecision>();
  for (const turn of state.policy.turns) {
    if (!state.hidden.has(turn.id)) {
      edits.set(
        turn.id,
        busyReason === undefined
          ? canRewindFromTurn(state.policy, turn)
          : { ok: false, reason: busyReason },
      );
    }
  }
  return edits;
}

type DeliveryTurn = { id: string; status: string; inboxId: string | null };

/**
 * Turns OpenCode holds no message for, which cannot bound a rewind or a fork:
 * prompts still queued, and ones stopped or failed before delivery. Those keep
 * an inbox ID for cleanup, so only OpenCode's messages tell them apart; without
 * a loaded transcript they are assumed delivered.
 */
export function unsentTurnIds(
  turns: readonly DeliveryTurn[],
  delivered: ReadonlySet<string>,
  transcriptLoaded: boolean,
): Set<string> {
  const sent = (turn: DeliveryTurn) => {
    if (delivered.has(deterministicPromptId(turn.id))) {
      return true;
    }
    if (turn.inboxId === null) {
      return false;
    }
    if (delivered.has(turn.inboxId) || turn.status === 'running' || turn.status === 'done') {
      return true;
    }
    return !transcriptLoaded && (turn.status === 'interrupted' || turn.status === 'failed');
  };
  return new Set(turns.filter((turn) => !sent(turn)).map((turn) => turn.id));
}

/**
 * OpenCode's Undo target: the last shown prompt OpenCode received, which is
 * the one before a waiting rewind's boundary. `turns` are in index order.
 */
export function undoTargetTurn<T extends { id: string }>(
  turns: readonly T[],
  hidden: ReadonlySet<string>,
  unsent: ReadonlySet<string>,
): T | undefined {
  return turns.findLast((turn) => !hidden.has(turn.id) && !unsent.has(turn.id));
}

/** Discarded turns, plus a waiting rewind's boundary turn and every later one. */
export function hiddenTurnIds(
  turns: readonly RewindTurn[],
  rewindTurnId?: string | null,
): Set<string> {
  const boundary = rewindTurnId ? turns.find((turn) => turn.id === rewindTurnId) : undefined;
  return new Set(
    turns
      .filter(
        (turn) =>
          Boolean(turn.discardedAt) ||
          // A send the runtime never got is the page's own row, not work the edit discards.
          (boundary !== undefined && turn.index >= boundary.index && turn.unsent !== true),
      )
      .map((turn) => turn.id),
  );
}

/**
 * Who a timeline anchor belongs to. A prompt without a turn is a message no
 * turn row owns; an assistant message's turn is known only once every owner
 * map is built.
 */
export type TimelineAnchorOwner =
  | { kind: 'prompt'; turnId?: string }
  | { kind: 'turn'; turnId: string }
  | { kind: 'message'; messageId: string };

/**
 * Drop hidden turns' anchors before they run, so hidden content never reaches
 * the fold's dedupe state (a hidden error card would swallow a later identical
 * one). Anchors without a known turn, such as event errors and form cards,
 * belong to the latest prompt before them.
 */
export function visibleTimelineAnchors<T extends { owner?: TimelineAnchorOwner }>(
  anchors: readonly T[],
  hidden: ReadonlySet<string>,
  messageTurn: (messageId: string) => string | undefined,
): readonly T[] {
  if (hidden.size === 0) {
    return anchors;
  }
  let afterHiddenPrompt = false;
  return anchors.filter(({ owner }) => {
    switch (owner?.kind) {
      case 'prompt':
        afterHiddenPrompt = owner.turnId !== undefined && hidden.has(owner.turnId);
        return !afterHiddenPrompt;
      case 'turn':
        return !hidden.has(owner.turnId);
      case 'message': {
        const turnId = messageTurn(owner.messageId);
        return turnId === undefined ? !afterHiddenPrompt : !hidden.has(turnId);
      }
      default:
        return !afterHiddenPrompt;
    }
  });
}
